// ─────────────────────────────────────────────────────────────────────────────
// The Hush — the narrative spine of the Conjugarden.
//
// The living web of language is the Verdant. A blight called the Hush is
// silencing it: words lose their sounds, verbs forget how to change, the world
// greys and goes mute. You are a Warden, woken from the Seedvault by Vey — the
// last Seedkeeper, a small luminous spore-spirit who remembers the old words.
//
// Every correct answer SPEAKS A WORD BACK INTO BEING and pushes the Hush back.
// Mastering a verb reseeds a species. Clearing a biome breaks the Hush's hold.
// Story progress is therefore a pure function of how you've played.
// ─────────────────────────────────────────────────────────────────────────────
import { type GardenState, masteredSpecimens, type BiomeId } from './garden.js'

export const COMPANION = { name: 'Vey', role: 'the last Seedkeeper', glyph: '✨' }

// Restoration is the heart of the story: how much of the Verdant you've sung back.
export function storyPoints(s: GardenState): number {
  const spec = masteredSpecimens(s).length
  const clears = Object.values(s.clears).reduce((a, b) => a + (b ?? 0), 0)
  return s.stats.totalCorrect + spec * 15 + clears * 40
}

// Asymptotic curve — the last of the Hush is always the hardest to dispel.
export function restorationPct(s: GardenState): number {
  const p = storyPoints(s)
  return Math.min(100, Math.round(100 * (1 - Math.exp(-p / 650))))
}
export function hushPct(s: GardenState): number {
  return 100 - restorationPct(s)
}

export type Act = {
  num: number
  roman: string
  id: string
  title: string
  biome?: BiomeId
  unlockAt: number          // story points
  tagline: string
  intro: string[]           // shown when the act begins
  objective: string         // what to do now
  done: string[]            // shown when the act is cleared
  lore: string              // a fragment for the Codex
}

export const ACTS: Act[] = [
  {
    num: 1, roman: 'I', id: 'waking-seed', title: 'The Waking Seed', biome: 'meadow', unlockAt: 0,
    tagline: 'A grey meadow, and a voice that remembers.',
    intro: [
      'You wake in a meadow the colour of ash. No birdsong. No wind-words. Only a small light, hovering.',
      '“Oh — you woke.” The light circles you. “I am Vey, the last Seedkeeper. The Hush took the rest.”',
      '“It cannot kill, Warden. It does something crueller — it makes the world forget how to speak. But you remember. Say a word back, and watch.”',
    ],
    objective: 'Speak words back in the Sunmeadow. Grow your first specimens to prove the Hush can be undone.',
    done: [
      'A single blade of grass remembers its green. Then a hundred. Vey spins, delighted. “You see? Every word you speak true, the Verdant takes back.”',
    ],
    lore: 'The Verdant is not a place but a weave — every living word a thread. The Hush unpicks it, thread by thread, until only grey remains.',
  },
  {
    num: 2, roman: 'II', id: 'river-remembers', title: 'The River Remembers', biome: 'riverlands', unlockAt: 120,
    tagline: 'The river of the past has gone mute.',
    intro: [
      'Vey leads you to a riverbed of cracked, silent stones. “This was the Riverlands — where the world keeps what it *was*.”',
      '“The Hush drank the past tense first. Without it, nothing remembers it ever happened. Give the river its voice. Speak what *was*.”',
    ],
    objective: 'Reclaim the Riverlands. Restore the past tenses until the river runs and remembers.',
    done: ['Water finds its old channel and begins, hesitantly, to murmur. The murmur becomes a story. “It remembers,” Vey breathes.'],
    lore: 'Seedkeepers say the river holds every word ever truly spoken. When it ran dry, whole histories went unspoken — and so, unhappened.',
  },
  {
    num: 3, roman: 'III', id: 'wind-would-say', title: 'What the Wind Would Say', biome: 'highlands', unlockAt: 320,
    tagline: 'On the ridges, every maybe blew silent.',
    intro: [
      'The Windhighlands stand bare and breathless. “Up here lives what *will* be, and what *might*,” says Vey, smaller now against the grey sky.',
      '“The Hush smothered the wind. No futures, no maybes — only a flat, certain nothing. Give the air its conditionals back. Let it wonder again.”',
    ],
    objective: 'Climb the Windhighlands. Restore the future and conditional until the wind can say “perhaps” again.',
    done: ['A breeze stirs — then gusts, carrying a hundred half-promises. Vey laughs into it. “Hear that? The world is allowed to *maybe* again.”'],
    lore: 'A language without a future tense is a people who have stopped imagining one. The Hush knows this. It always takes the future early.',
  },
  {
    num: 4, roman: 'IV', id: 'doubting-fog', title: 'The Doubting Fog', biome: 'mistwood', unlockAt: 600,
    tagline: 'Where nothing is certain, the Hush grows fat.',
    intro: [
      'The Mistwood swallows your boots in fog. Vey’s light barely carries. “Careful. This is the subjunctive — the mood of doubt, of wishing, of *if only*.”',
      '“The Hush feeds on uncertainty, and here it feasts. To clear this fog you must speak the unspoken — the things that are wished, not known.”',
    ],
    objective: 'Brave the Mistwood. Restore the subjunctive moods and starve the Hush of doubt.',
    done: ['The fog thins to silver lace. Shapes resolve: trees, paths, a way through. “You spoke through the doubt,” Vey says, quieter. “Few can.”'],
    lore: 'The oldest Wardens feared the Mistwood most. It is easy to speak what is. It is the bravest grammar to speak what merely might be.',
  },
  {
    num: 5, roman: 'V', id: 'eldest-canopy', title: 'The Eldest Canopy', biome: 'canopy', unlockAt: 950,
    tagline: 'The source. Where time is layered, and the Hush was born.',
    intro: [
      'You climb into the Highcanopy, where the oldest trees hold time in layers — what *has* happened, what *had* happened before that.',
      'Vey’s light flickers. “This is where it began, Warden. The Hush started in the perfect tenses — the deepest, most layered words. Reach the heart. Speak it back, or it speaks us all away.”',
    ],
    objective: 'Reach the heart of the Highcanopy. Restore the perfect tenses and confront the Hush at its source.',
    done: ['At the canopy’s crown the grey curls back like burnt paper. The Hush does not roar. It simply — quiets. And then the Verdant is loud with everything at once.'],
    lore: 'The Hush was never an invader. It was the silence that grows wherever words go unspoken. It can never be killed. Only out-spoken.',
  },
  {
    num: 6, roman: 'VI', id: 'verdant-remade', title: 'The Verdant Remade', unlockAt: 1400,
    tagline: 'The Hush is broken. Now the garden must be kept.',
    intro: [
      'The grey is gone. The Verdant is green to every horizon, loud with words you helped speak back.',
      '“It will return,” Vey says, settling on your shoulder. “Silence always does, wherever speaking stops. But now there is a Warden again. Keep tending. Keep speaking. That is the whole of it.”',
    ],
    objective: 'The Verdant is restored — but a garden is never finished. Keep tending: deepen your mastery and grow the rarest species.',
    done: ['You are the Warden of the Verdant. There is no end to the keeping — only the next word, spoken true.'],
    lore: 'Epilogue. The Seedvault is full again. Somewhere, a new seed waits to wake the next Warden, should the silence ever win.',
  },
]

export function currentAct(s: GardenState): Act {
  const p = storyPoints(s)
  let act = ACTS[0]
  for (const a of ACTS) if (p >= a.unlockAt) act = a
  return act
}

export function nextAct(s: GardenState): Act | null {
  const cur = currentAct(s)
  return ACTS.find(a => a.num === cur.num + 1) ?? null
}

// Objective progress toward the next act (or 100% restoration in the finale).
export function actObjective(s: GardenState): { label: string; have: number; need: number; pct: number } {
  const p = storyPoints(s)
  const cur = currentAct(s)
  const nxt = nextAct(s)
  if (!nxt) {
    const r = restorationPct(s)
    return { label: cur.objective, have: r, need: 100, pct: r }
  }
  const have = Math.max(0, p - cur.unlockAt)
  const need = nxt.unlockAt - cur.unlockAt
  return { label: cur.objective, have, need, pct: Math.min(100, Math.round((have / need) * 100)) }
}

// ── Story beats (scenes that fire as you cross thresholds) ───────────────────
export type Beat = {
  id: string
  kind: 'act' | 'milestone'
  title: string
  lines: string[]
  speaker: string | null
  cond: (s: GardenState) => boolean
  actNum?: number
}

const MILESTONES: Beat[] = [
  {
    id: 'm-first-specimen', kind: 'milestone', title: 'A word takes root', speaker: COMPANION.name,
    lines: ['“There — feel that?” Vey hums. “A verb, fully alive again. It will conjugate on its own now, forever. One thread re-woven. Only the rest of the world to go.”'],
    cond: s => masteredSpecimens(s).length >= 1,
  },
  {
    id: 'm-words-50', kind: 'milestone', title: 'Fifty words spoken back', speaker: COMPANION.name,
    lines: ['“Fifty words.” Vey’s light is visibly brighter. “The Hush noticed that one. Keep going — it has started to listen for you.”'],
    cond: s => s.stats.totalCorrect >= 50,
  },
  {
    id: 'm-first-bloom', kind: 'milestone', title: 'A region breaks free', speaker: COMPANION.name,
    lines: ['You carry a whole biome home in bloom. “An entire region, un-Hushed,” Vey says, awed. “No Warden has done that in my long memory. The Verdant remembers your name now.”'],
    cond: s => s.stats.wins >= 1,
  },
  {
    id: 'm-words-200', kind: 'milestone', title: 'The Hush recoils', speaker: COMPANION.name,
    lines: ['Two hundred words and the grey is in retreat. For the first time, Vey looks not at the world but at you. “You are no longer waking up, Warden. You are winning.”'],
    cond: s => s.stats.totalCorrect >= 200,
  },
  {
    id: 'm-restoration-50', kind: 'milestone', title: 'Half the world remembers', speaker: COMPANION.name,
    lines: ['Half the Verdant has its voice again. The silence no longer feels inevitable. “We are past the turning,” Vey says softly. “Downhill now — but downhill is still a climb.”'],
    cond: s => restorationPct(s) >= 50,
  },
  {
    id: 'm-restoration-100', kind: 'milestone', title: 'The Verdant, whole', speaker: COMPANION.name,
    lines: ['Not a thread of grey remains. The world is so loud with words it nearly knocks you down. Vey is weeping light. “You spoke it all back. All of it.”'],
    cond: s => restorationPct(s) >= 100,
  },
]

export function allBeats(): Beat[] {
  const actBeats: Beat[] = ACTS.map(a => ({
    id: 'act-' + a.id, kind: 'act' as const, title: `Act ${a.roman} · ${a.title}`,
    lines: a.intro, speaker: a.num === 1 ? COMPANION.name : null, actNum: a.num,
    cond: (s: GardenState) => storyPoints(s) >= a.unlockAt,
  }))
  // Interleave by the point at which each becomes true, roughly: acts gate by points,
  // milestones by their own conditions. Order: act unlock first, then its milestones.
  return [actBeats[0], ...MILESTONES.slice(0, 3), actBeats[1], actBeats[2], MILESTONES[3], MILESTONES[4], actBeats[3], actBeats[4], actBeats[5], MILESTONES[5]]
}

// Beats whose condition is met but which the player hasn't seen yet.
export function pendingBeats(s: GardenState): Beat[] {
  const seen = new Set(s.seenBeats)
  return allBeats().filter(b => !seen.has(b.id) && b.cond(s))
}
